export type ApiMethod = "GET" | "POST";
export type ApiAuth = "없음" | "세션 토큰" | "API Key";

export interface ApiParameter {
  name: string;
  location: "Path" | "Query" | "Header" | "Body" | "Form";
  required: boolean;
  type: string;
  description: string;
  example?: string;
}

export interface ApiSpec {
  id: string;
  category: "인증" | "스냅샷" | "서울버스로그" | "업데이트" | "dalgoT 운영";
  title: string;
  method: ApiMethod;
  path: string;
  auth: ApiAuth;
  contentType: string;
  description: string;
  parameters: ApiParameter[];
  requestExample?: string;
  responseExample: string;
  note?: string;
}

const jsonHeader: ApiParameter = {
  name: "Content-Type",
  location: "Header",
  required: true,
  type: "string",
  description: "JSON 요청 본문을 사용할 때 지정합니다.",
  example: "application/json",
};

const apiKeyHeader: ApiParameter = {
  name: "key",
  location: "Header",
  required: true,
  type: "string",
  description: "관리자에게 발급받은 API Key 값을 전달합니다.",
  example: "<API_KEY>",
};

export const apiCatalog: ApiSpec[] = [
  {
    id: "dalgot-brightness",
    category: "dalgoT 운영",
    title: "운영 콘솔 밝기·수동 차량 근접 조회",
    method: "GET",
    path: "/dalgot/operations/brightness",
    auth: "API Key",
    contentType: "application/json",
    description: "저장된 미리보기 시각 기준 밝기(cd)와 수동 차량 근접 상태를 조회합니다. 현재 시각 모드는 한국시간 기준으로 계산합니다.",
    parameters: [apiKeyHeader],
    requestExample: `curl 'https://ai.addd.co.kr/api/dalgot/operations/brightness' -H 'key: <API_KEY>'`,
    responseExample: `{"cd":200,"unit":"cd/㎡","manualVehicleProximity":true,"previewTime":"19:25","effectiveTime":"19:25","timeMode":"preview","brightnessMode":"night","date":"2026-10-15","timezone":"Asia/Seoul","sunsetTime":"17:56","updatedAt":"2026-10-15T05:30:00.000Z"}`,
    note: "09:00 이상~서울 일몰 미만은 2,000cd/㎡, 나머지는 200cd/㎡입니다. 수동 차량 근접은 밝기와 독립적인 boolean 값입니다. 모든 차량에 공통 적용됩니다.",
  },
  {
    id: "dalgot-brightness-save",
    category: "dalgoT 운영",
    title: "운영 콘솔 시각·수동 차량 근접 저장",
    method: "POST",
    path: "/dalgot/operations/brightness",
    auth: "API Key",
    contentType: "application/json",
    description: "시각 미리보기 및 수동 차량 근접 상태를 저장합니다. 전달한 필드만 갱신하고 나머지 설정은 유지합니다.",
    parameters: [apiKeyHeader, jsonHeader,
      { name: "previewTime", location: "Body", required: false, type: "string | null", description: "00:00~23:59. null이면 실제 한국시간 사용", example: "19:25" },
      { name: "manualVehicleProximity", location: "Body", required: false, type: "boolean", description: "수동 차량 근접 ON/OFF", example: "true" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/dalgot/operations/brightness' -H 'key: <API_KEY>' -H 'Content-Type: application/json' -d '{"previewTime":"19:25","manualVehicleProximity":true}'`,
    responseExample: `{"cd":200,"unit":"cd/㎡","manualVehicleProximity":true,"previewTime":"19:25","effectiveTime":"19:25","timeMode":"preview","brightnessMode":"night","date":"2026-10-15","timezone":"Asia/Seoul","sunsetTime":"17:56","updatedAt":"2026-10-15T05:30:00.000Z"}`,
    note: "화면에서는 시각 저장 버튼으로 시간을 적용하고, 수동 차량 근접 토글은 즉시 저장됩니다. 잘못된 입력은 HTTP 400입니다.",
  },
  {
    id: "auth-login",
    category: "인증",
    title: "로그인",
    method: "POST",
    path: "/auth/login",
    auth: "없음",
    contentType: "application/json",
    description: "이메일과 비밀번호를 검증하고 이후 요청에 사용할 세션 토큰을 발급합니다.",
    parameters: [
      jsonHeader,
      { name: "email", location: "Body", required: true, type: "string", description: "사용자 이메일", example: "admin@addd.co.kr" },
      { name: "password", location: "Body", required: true, type: "string", description: "사용자 비밀번호", example: "********" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/auth/login' \\
  -H 'Content-Type: application/json' \\
  -d '{"email":"admin@addd.co.kr","password":"********"}'`,
    responseExample: `{"status":200,"message":"로그인 성공","data":"<TOKEN>"}`,
  },
  {
    id: "auth-profile",
    category: "인증",
    title: "로그인 사용자 조회",
    method: "GET",
    path: "/auth/profile",
    auth: "세션 토큰",
    contentType: "application/json",
    description: "Authorization 헤더의 로그인 토큰으로 현재 사용자와 권한 그룹을 조회합니다.",
    parameters: [
      { name: "Authorization", location: "Header", required: true, type: "string", description: "로그인 API가 반환한 토큰", example: "<TOKEN>" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/auth/profile' -H 'Authorization: <TOKEN>'`,
    responseExample: `{"status":200,"data":{"name":"사용자","userGroup":"ai"}}`,
  },
  {
    id: "snapshot-upload",
    category: "스냅샷",
    title: "기기 스냅샷 업로드",
    method: "POST",
    path: "/adddi-light-registration/sendImage",
    auth: "없음",
    contentType: "multipart/form-data",
    description: "기기 식별번호와 이미지 파일을 받아 최신 스냅샷으로 저장합니다. 파일은 최대 20MB입니다.",
    parameters: [
      { name: "mediaNumber", location: "Query", required: true, type: "string", description: "매체 번호 또는 기기 식별값", example: "1023" },
      { name: "file", location: "Form", required: true, type: "file", description: "업로드할 이미지 파일", example: "snapshot.jpg" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/adddi-light-registration/sendImage?mediaNumber=1023' \\
  -F 'file=@snapshot.jpg'`,
    responseExample: `{"status":200,"message":"ADDDI LIGHT 이미지 업로드 성공","data":"DEVICE_IMAGE.jpg"}`,
    note: "동일한 기기 식별값으로 다시 전송하면 기존 스냅샷 파일이 최신 이미지로 교체됩니다.",
  },
  {
    id: "snapshot-list",
    category: "스냅샷",
    title: "스냅샷 목록 조회",
    method: "GET",
    path: "/adddi-light-registration/imageList",
    auth: "없음",
    contentType: "application/json",
    description: "수정 시각이 최신인 순서로 스냅샷 파일 목록을 페이지 단위로 조회합니다.",
    parameters: [
      { name: "page", location: "Query", required: true, type: "number", description: "페이지 번호", example: "1" },
      { name: "limit", location: "Query", required: true, type: "number", description: "페이지당 개수, 최대 100", example: "8" },
      { name: "filter", location: "Query", required: false, type: "string", description: "파일명 또는 기기 식별값 검색", example: "68EDA4" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/adddi-light-registration/imageList?page=1&limit=8&filter='`,
    responseExample: `{"status":200,"data":[1,[["DEVICE_IMAGE.jpg","2026-08-14 14:30:00"]]]}`,
  },
  {
    id: "status-ingest",
    category: "서울버스로그",
    title: "디바이스 상태 로그 수신",
    method: "POST",
    path: "/data/statusLog",
    auth: "없음",
    contentType: "application/json",
    description: "기기 로그를 저장하고 디바이스의 최근 수신 시각, 버전, 마지막 로그와 오류 상태를 갱신합니다.",
    parameters: [
      jsonHeader,
      { name: "id", location: "Body", required: true, type: "string", description: "디바이스 ID", example: "SEOUL_BUS_001" },
      { name: "mac", location: "Body", required: true, type: "string", description: "디바이스 MAC 주소", example: "68EDA485D78A" },
      { name: "status", location: "Body", required: true, type: "string", description: "저장할 원본 로그 메시지", example: "[정상] 메인앱 업데이터 가동: v0.3.7" },
      { name: "test", location: "Body", required: false, type: "boolean | 0 | 1", description: "TEST 환경 로그 여부", example: "false" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/data/statusLog' \\
  -H 'Content-Type: application/json' \\
  -d '{"id":"SEOUL_BUS_001","mac":"68EDA485D78A","status":"[정상] 메인앱 업데이터 가동: v0.3.7","test":false}'`,
    responseExample: `true`,
    note: "업데이터 가동 및 APP START 형식의 로그는 각각 업데이터 버전과 메인앱 버전으로 자동 파싱됩니다.",
  },
  {
    id: "status-list",
    category: "서울버스로그",
    title: "디바이스 상태 목록 조회",
    method: "GET",
    path: "/data/statusLogList",
    auth: "없음",
    contentType: "application/json",
    description: "물리 디바이스를 MAC 기준으로 통합해 최근 상태와 오류 집계를 페이지 단위로 반환합니다.",
    parameters: [
      { name: "date", location: "Query", required: true, type: "number", description: "조회 기간: 오늘 1, 일주일 7, 전체 0", example: "1" },
      { name: "type1", location: "Query", required: true, type: "main | test", description: "main은 메인만, test는 메인과 테스트 통합", example: "main" },
      { name: "type2", location: "Query", required: true, type: "0 | 1", description: "1은 부팅 전체, 0은 최초 설치 로그가 있는 기기", example: "1" },
      { name: "page", location: "Query", required: false, type: "number", description: "페이지 번호", example: "1" },
      { name: "limit", location: "Query", required: false, type: "number", description: "페이지당 개수, 최대 100", example: "10" },
      { name: "mac", location: "Query", required: false, type: "string", description: "MAC 주소 부분 검색", example: "68EDA4" },
      { name: "updaterVersion", location: "Query", required: false, type: "string", description: "업데이터 버전 또는 unknown", example: "v0.3.7" },
      { name: "mainAppVersion", location: "Query", required: false, type: "string", description: "메인앱 버전 또는 unknown", example: "v1.4.2" },
      { name: "statusFilter", location: "Query", required: false, type: "all | normal | warning", description: "상태 집계 필터", example: "all" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/data/statusLogList?date=1&type1=main&type2=1&page=1&limit=10'`,
    responseExample: `{"items":[],"pagination":{"page":1,"limit":10,"total":0,"totalPages":1},"summary":{"total":0,"normal":0,"warning":0}}`,
  },
  {
    id: "status-detail",
    category: "서울버스로그",
    title: "디바이스 상세 로그 조회",
    method: "GET",
    path: "/data/statusLogDetail",
    auth: "없음",
    contentType: "application/json",
    description: "선택한 디바이스의 원본 로그를 최신순으로 조회합니다.",
    parameters: [
      { name: "id", location: "Query", required: false, type: "string", description: "MAC이 없을 때 사용할 디바이스 ID", example: "SEOUL_BUS_001" },
      { name: "mac", location: "Query", required: false, type: "string", description: "우선 적용되는 디바이스 MAC 주소", example: "68EDA485D78A" },
      { name: "type1", location: "Query", required: false, type: "main | test", description: "main이면 TEST 로그 제외", example: "main" },
      { name: "page", location: "Query", required: false, type: "number", description: "페이지 번호", example: "1" },
      { name: "limit", location: "Query", required: false, type: "number", description: "페이지당 개수, 최대 100", example: "10" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/data/statusLogDetail?mac=68EDA485D78A&type1=main&page=1&limit=10'`,
    responseExample: `{"items":[{"statusLogId":1,"status":"APP START","createDate":"2026-08-14T05:30:00.000Z","test":false}],"pagination":{"page":1,"limit":10,"total":1,"totalPages":1}}`,
  },
  {
    id: "status-updater-versions",
    category: "서울버스로그",
    title: "업데이터 버전 목록",
    method: "GET",
    path: "/data/statusLogVersions",
    auth: "없음",
    contentType: "application/json",
    description: "현재 디바이스 관리 테이블에 저장된 업데이터 버전을 중복 없이 최신순으로 반환합니다.",
    parameters: [],
    requestExample: `curl 'https://ai.addd.co.kr/api/data/statusLogVersions'`,
    responseExample: `["v0.3.7","v0.3.6"]`,
  },
  {
    id: "status-main-versions",
    category: "서울버스로그",
    title: "메인앱 버전 목록",
    method: "GET",
    path: "/data/statusLogMainAppVersions",
    auth: "없음",
    contentType: "application/json",
    description: "현재 디바이스 관리 테이블에 저장된 메인앱 버전을 중복 없이 최신순으로 반환합니다.",
    parameters: [],
    requestExample: `curl 'https://ai.addd.co.kr/api/data/statusLogMainAppVersions'`,
    responseExample: `["v1.4.2","v1.4.1"]`,
  },
  {
    id: "status-export",
    category: "서울버스로그",
    title: "상태 로그 엑셀 다운로드",
    method: "POST",
    path: "/data/downloadStatusLog",
    auth: "없음",
    contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    description: "디바이스 상태 로그를 XLSX 파일로 생성해 반환합니다.",
    parameters: [],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/data/downloadStatusLog' -o statusLog.xlsx`,
    responseExample: `<binary xlsx>`,
  },
  {
    id: "update-services",
    category: "업데이트",
    title: "서비스 및 파일 유형 목록",
    method: "GET",
    path: "/update/serviceList",
    auth: "API Key",
    contentType: "application/json",
    description: "UPDATE 저장소의 서비스 폴더와 서비스별 파일 유형을 조회합니다.",
    parameters: [apiKeyHeader],
    requestExample: `curl 'https://ai.addd.co.kr/api/update/serviceList' -H 'key: <API_KEY>'`,
    responseExample: `{"ADDDILIGHTSBA":["APP","FIRMWARE"]}`,
  },
  {
    id: "update-latest-selection",
    category: "업데이트",
    title: "최근 업로드 조합 조회",
    method: "GET",
    path: "/update/latestSelection",
    auth: "API Key",
    contentType: "application/json",
    description: "TEST와 MAIN 전체에서 가장 최근에 업로드된 파일의 서비스와 파일 유형을 반환합니다.",
    parameters: [apiKeyHeader],
    requestExample: `curl 'https://ai.addd.co.kr/api/update/latestSelection' -H 'key: <API_KEY>'`,
    responseExample: `{"service":"ADDDILIGHTSBA","type":"APP","uploadedAt":"2026-08-07T05:12:41.534Z"}`,
  },
  {
    id: "update-files",
    category: "업데이트",
    title: "업데이트 파일 목록",
    method: "GET",
    path: "/update/fileList",
    auth: "API Key",
    contentType: "application/json",
    description: "선택한 서비스와 파일 유형의 TEST·MAIN 파일을 버전 내림차순으로 반환합니다.",
    parameters: [
      apiKeyHeader,
      { name: "service", location: "Query", required: true, type: "string", description: "서비스 폴더명", example: "ADDDILIGHTSBA" },
      { name: "type", location: "Query", required: true, type: "string", description: "파일 유형", example: "APP" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/update/fileList?service=ADDDILIGHTSBA&type=APP' -H 'key: <API_KEY>'`,
    responseExample: `[{"name":"ADDDILIGHTSBA_APP_1.9.1.zip","path":"ADDDILIGHTSBA/APP/MAIN/ADDDILIGHTSBA_APP_1.9.1.zip","test":false}]`,
  },
  {
    id: "update-upload",
    category: "업데이트",
    title: "업데이트 파일 업로드",
    method: "POST",
    path: "/update/upload/:service",
    auth: "API Key",
    contentType: "multipart/form-data",
    description: "업데이트 파일을 선택한 서비스와 유형의 TEST 폴더에 업로드합니다.",
    parameters: [
      apiKeyHeader,
      { name: "service", location: "Path", required: true, type: "string", description: "서비스명", example: "ADDDILIGHTSBA" },
      { name: "type", location: "Form", required: true, type: "string", description: "파일 유형", example: "APP" },
      { name: "file", location: "Form", required: true, type: "file", description: "버전 규칙에 맞는 업데이트 파일", example: "ADDDILIGHTSBA_APP_1.9.2.zip" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/update/upload/ADDDILIGHTSBA' \\
  -H 'key: <API_KEY>' -F 'type=APP' -F 'file=@ADDDILIGHTSBA_APP_1.9.2.zip'`,
    responseExample: `true`,
    note: "파일명 마지막 구간은 1.2.3.zip과 같은 버전 형식이어야 하며 업로드 후 상태는 TEST입니다.",
  },
  {
    id: "update-change-status",
    category: "업데이트",
    title: "TEST·MAIN 상태 변경",
    method: "POST",
    path: "/update/changeStatus",
    auth: "API Key",
    contentType: "application/json",
    description: "파일을 TEST에서 MAIN으로 배포하거나 MAIN에서 TEST로 철회합니다.",
    parameters: [
      apiKeyHeader,
      jsonHeader,
      { name: "filepath", location: "Body", required: true, type: "string", description: "서비스부터 시작하는 상대 파일 경로", example: "ADDDILIGHTSBA/APP/TEST/ADDDILIGHTSBA_APP_1.9.2.zip" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/update/changeStatus' \\
  -H 'key: <API_KEY>' -H 'Content-Type: application/json' \\
  -d '{"filepath":"ADDDILIGHTSBA/APP/TEST/ADDDILIGHTSBA_APP_1.9.2.zip"}'`,
    responseExample: `{"status":200,"message":"상태를 변경했습니다."}`,
    note: "다운로드 기록이 존재하는 MAIN 파일은 TEST로 철회할 수 없습니다.",
  },
  {
    id: "update-version",
    category: "업데이트",
    title: "최신 버전 조회",
    method: "GET",
    path: "/update/version/:service",
    auth: "API Key",
    contentType: "text/plain",
    description: "서비스, 파일 유형, 배포 상태에 해당하는 최신 파일의 버전 문자열을 반환합니다.",
    parameters: [
      apiKeyHeader,
      { name: "service", location: "Path", required: true, type: "string", description: "서비스명", example: "ADDDILIGHTSBA" },
      { name: "type", location: "Query", required: true, type: "string", description: "파일 유형", example: "APP" },
      { name: "test", location: "Query", required: true, type: "0 | 1", description: "1은 TEST, 그 외는 MAIN", example: "0" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/update/version/ADDDILIGHTSBA?type=APP&test=0' -H 'key: <API_KEY>'`,
    responseExample: `"1.9.1"`,
  },
  {
    id: "update-download-client",
    category: "업데이트",
    title: "기기용 최신 파일 다운로드",
    method: "POST",
    path: "/update/download/:service",
    auth: "API Key",
    contentType: "application/json → binary",
    description: "기기가 사용할 최신 MAIN 파일을 반환하며 test=1이면 TEST 파일도 후보에 포함합니다.",
    parameters: [
      apiKeyHeader,
      jsonHeader,
      { name: "service", location: "Path", required: true, type: "string", description: "서비스명", example: "ADDDILIGHTSBA" },
      { name: "type", location: "Body", required: true, type: "string", description: "파일 유형", example: "APP" },
      { name: "test", location: "Body", required: false, type: "0 | 1", description: "1이면 TEST 파일 포함", example: "0" },
      { name: "authNumber", location: "Body", required: false, type: "number", description: "다운로드 기록용 인증 번호", example: "1023" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/update/download/ADDDILIGHTSBA' \\
  -H 'key: <API_KEY>' -H 'Content-Type: application/json' \\
  -d '{"type":"APP","test":"0","authNumber":1023}' -o update.zip`,
    responseExample: `<binary update file>`,
  },
  {
    id: "update-download-one",
    category: "업데이트",
    title: "지정 파일 다운로드",
    method: "POST",
    path: "/update/downloadOne",
    auth: "API Key",
    contentType: "application/json → binary",
    description: "관리 화면에서 지정한 상대 경로의 파일 하나를 다운로드합니다.",
    parameters: [
      apiKeyHeader,
      jsonHeader,
      { name: "path", location: "Body", required: true, type: "string", description: "다운로드할 상대 경로", example: "ADDDILIGHTSBA/APP/MAIN/ADDDILIGHTSBA_APP_1.9.1.zip" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/update/downloadOne' \\
  -H 'key: <API_KEY>' -H 'Content-Type: application/json' \\
  -d '{"path":"ADDDILIGHTSBA/APP/MAIN/ADDDILIGHTSBA_APP_1.9.1.zip"}' -o update.zip`,
    responseExample: `<binary update file>`,
  },
  {
    id: "update-delete",
    category: "업데이트",
    title: "TEST 파일 삭제",
    method: "POST",
    path: "/update/delete",
    auth: "API Key",
    contentType: "application/json",
    description: "해당 유형의 최신 TEST 파일을 DELETE 보관 폴더로 이동합니다.",
    parameters: [
      apiKeyHeader,
      jsonHeader,
      { name: "filepath", location: "Body", required: true, type: "string", description: "삭제할 TEST 파일 상대 경로", example: "ADDDILIGHTSBA/APP/TEST/ADDDILIGHTSBA_APP_1.9.2.zip" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/update/delete' \\
  -H 'key: <API_KEY>' -H 'Content-Type: application/json' \\
  -d '{"filepath":"ADDDILIGHTSBA/APP/TEST/ADDDILIGHTSBA_APP_1.9.2.zip"}'`,
    responseExample: `{"status":200,"message":"삭제했습니다."}`,
    note: "현재 구현상 각 파일 유형의 최신 TEST 파일만 삭제할 수 있습니다.",
  },
  {
    id: "update-save-service",
    category: "업데이트",
    title: "업데이트 서비스 추가",
    method: "POST",
    path: "/update/saveService",
    auth: "API Key",
    contentType: "application/json",
    description: "새 서비스 폴더와 기본 APP·FIRMWARE 유형을 생성합니다.",
    parameters: [
      apiKeyHeader,
      jsonHeader,
      { name: "service", location: "Body", required: true, type: "string", description: "새 서비스명", example: "NEW_DEVICE" },
    ],
    requestExample: `curl -X POST 'https://ai.addd.co.kr/api/update/saveService' \\
  -H 'key: <API_KEY>' -H 'Content-Type: application/json' -d '{"service":"NEW_DEVICE"}'`,
    responseExample: `{"status":200,"message":"제품 추가 완료"}`,
  },
  {
    id: "update-history",
    category: "업데이트",
    title: "파일 다운로드 기록",
    method: "GET",
    path: "/update/updateHistory",
    auth: "API Key",
    contentType: "application/json",
    description: "지정한 MAIN 파일 경로에 저장된 기기 다운로드 기록을 조회합니다.",
    parameters: [
      apiKeyHeader,
      { name: "path", location: "Query", required: true, type: "string", description: "MAIN 파일 상대 경로", example: "ADDDILIGHTSBA/APP/MAIN/ADDDILIGHTSBA_APP_1.9.1.zip" },
    ],
    requestExample: `curl 'https://ai.addd.co.kr/api/update/updateHistory?path=ADDDILIGHTSBA%2FAPP%2FMAIN%2FADDDILIGHTSBA_APP_1.9.1.zip' -H 'key: <API_KEY>'`,
    responseExample: `[{"updateHistoryId":1,"authNumber":1023,"path":"ADDDILIGHTSBA/APP/MAIN/ADDDILIGHTSBA_APP_1.9.1.zip"}]`,
  },
];

export const apiCategories = ["인증", "스냅샷", "서울버스로그", "업데이트", "dalgoT 운영"] as const;
